import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sidebar } from "@/components/Layout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { usePaywall } from "@/components/PaywallContext";
import { getTodayStr } from "@/lib/utils";
import { 
  TrendingUp, 
  Receipt, 
  CreditCard,
  ArrowLeft,
  Check,
  Repeat,
  Calendar as CalendarIcon,
  Coffee,
  ShoppingBag,
  AlertCircle
} from "lucide-react";

const CATEGORIES = [
  { value: "housing", label: "Housing" },
  { value: "utilities", label: "Utilities" },
  { value: "food", label: "Food & Groceries" },
  { value: "transport", label: "Transport" },
  { value: "entertainment", label: "Entertainment" },
  { value: "healthcare", label: "Healthcare" },
  { value: "insurance", label: "Insurance" },
  { value: "subscriptions", label: "Subscriptions" },
  { value: "other", label: "Other" }
];

const FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "fourweekly", label: "Every 4 weeks (28 days)" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" }
];

export default function AddTransactionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { triggerFirstTransaction } = usePaywall();
  const [type, setType] = useState(null); // 'income', 'bill', 'debt'
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isRecurring, setIsRecurring] = useState(true);
  
  const [formData, setFormData] = useState({
    name: "",
    amount: "",
    frequency: "monthly",
    startDate: getTodayStr(),
    category: "",
    totalDebt: "",
    interestRate: "",
    isDebtPayment: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!type) return;
    
    setLoading(true);
    try {
      if (type === 'income') {
        await axios.post(`${API}/income`, {
          name: formData.name,
          amount: parseFloat(formData.amount),
          frequency: isRecurring ? formData.frequency : 'once',
          next_pay_date: formData.startDate,
          is_recurring: isRecurring
        }, { withCredentials: true });
        
        // Trigger paywall after first income added
        triggerFirstTransaction('income');
      } else {
        // Bill or debt payment (based on toggle)
        await axios.post(`${API}/bills`, {
          name: formData.name,
          amount: parseFloat(formData.amount),
          currency: user?.currency || 'GBP',
          category: formData.category || 'other',
          frequency: isRecurring ? formData.frequency : 'once',
          start_date: formData.startDate,
          is_debt: formData.isDebtPayment || false,
          total_debt: formData.isDebtPayment ? parseFloat(formData.totalDebt) || null : null,
          interest_rate: formData.isDebtPayment ? parseFloat(formData.interestRate) || null : null,
          is_recurring: isRecurring
        }, { withCredentials: true });
        
        // Trigger paywall after first bill added
        triggerFirstTransaction('bill');
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/calendar');
      }, 1500);
    } catch (error) {
      console.error('Error creating transaction:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setType(null);
    setIsRecurring(true);
    setFormData({
      name: "",
      amount: "",
      frequency: "monthly",
      startDate: getTodayStr(),
      category: "",
      totalDebt: "",
      interestRate: "",
      isDebtPayment: false
    });
  };

  if (success) {
    return (
      <Sidebar>
        <div className="max-w-lg mx-auto py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-p2p-success/10 flex items-center justify-center mx-auto mb-4 animate-bounce-subtle">
            <Check className="w-8 h-8 text-p2p-success" />
          </div>
          <h2 className="font-manrope text-2xl font-semibold text-p2p-teal mb-2">Added successfully</h2>
          <p className="text-p2p-muted">Taking you back to your calendar...</p>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="max-w-lg mx-auto">
        {/* Back button when type is selected */}
        {type && (
          <button
            onClick={resetForm}
            className="flex items-center gap-2 text-p2p-muted hover:text-p2p-teal mb-6 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
        )}

        {/* Type Selection */}
        {!type ? (
          <div className="py-8">
            <h1 className="font-manrope text-2xl sm:text-3xl font-semibold text-p2p-teal mb-2">
              What do you want to add?
            </h1>
            <p className="text-p2p-muted mb-8">
              Track your money coming in and going out
            </p>

            <div className="space-y-3">
              {/* Income Option */}
              <button
                onClick={() => setType('income')}
                className="w-full flex items-center gap-4 p-5 rounded-2xl border-2 border-p2p-border bg-white hover:border-emerald-400/50 hover:bg-emerald-50/50 transition-all text-left group"
                data-testid="select-income"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="font-semibold text-p2p-teal">Income</p>
                  <p className="text-sm text-p2p-muted">Salary, freelance, benefits</p>
                </div>
              </button>

              {/* Bill / Expense Option (Combined with Debt toggle inside) */}
              <button
                onClick={() => setType('bill')}
                className="w-full flex items-center gap-4 p-5 rounded-2xl border-2 border-p2p-border bg-white hover:border-red-400/50 hover:bg-red-50/50 transition-all text-left group"
                data-testid="select-bill"
              >
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Receipt className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-p2p-teal">Bill / Expense</p>
                  <p className="text-sm text-p2p-muted">Rent, utilities, subscriptions, debt payments</p>
                </div>
              </button>
            </div>
          </div>
        ) : (
          /* Form */
          <div className="py-4">
            <h1 className="font-manrope text-2xl font-semibold text-p2p-teal mb-6">
              {type === 'income' ? 'Add Income' : 'Add Bill / Expense'}
            </h1>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-p2p-muted">
                  {type === 'income' ? 'Income name' : 'Bill name'}
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={type === 'income' ? 'e.g. Monthly Salary' : 'e.g. Netflix'}
                  className="h-12 rounded-xl border-p2p-border focus:border-p2p-teal"
                  required
                  data-testid="input-name"
                />
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label htmlFor="amount" className="text-p2p-muted">Amount</Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-p2p-muted font-medium">
                    {user?.currency === 'USD' ? '$' : user?.currency === 'EUR' ? '€' : '£'}
                  </span>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    className="h-12 pl-8 rounded-xl border-p2p-border focus:border-p2p-teal"
                    required
                    data-testid="input-amount"
                  />
                </div>
              </div>

              {/* Recurring Toggle - PROMINENT */}
              <div className="p-4 rounded-2xl bg-p2p-surface-hover border border-p2p-border">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {isRecurring ? (
                      <div className="w-10 h-10 rounded-xl bg-p2p-teal/10 flex items-center justify-center">
                        <Repeat className="w-5 h-5 text-p2p-teal" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                        <CalendarIcon className="w-5 h-5 text-amber-500" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-p2p-teal">
                        {isRecurring ? 'Recurring' : 'One-off'}
                      </p>
                      <p className="text-xs text-p2p-muted">
                        {isRecurring ? 'Repeats automatically' : 'Just this once'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={isRecurring}
                    onCheckedChange={setIsRecurring}
                    data-testid="toggle-recurring"
                  />
                </div>

                {/* Frequency selector - only show when recurring */}
                {isRecurring && (
                  <div className="pt-3 border-t border-p2p-border/50">
                    <Label className="text-xs text-p2p-muted mb-2 block">How often?</Label>
                    <Select 
                      value={formData.frequency} 
                      onValueChange={(val) => setFormData({ ...formData, frequency: val })}
                    >
                      <SelectTrigger className="h-11 rounded-xl border-p2p-border bg-white" data-testid="select-frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.map(f => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* One-off examples */}
                {!isRecurring && (
                  <div className="pt-3 border-t border-p2p-border/50">
                    <p className="text-xs text-p2p-muted mb-2">Perfect for:</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white rounded-full text-xs text-p2p-muted border border-p2p-border/50">
                        <Coffee className="w-3 h-3" /> Coffee
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white rounded-full text-xs text-p2p-muted border border-p2p-border/50">
                        <ShoppingBag className="w-3 h-3" /> Shopping
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white rounded-full text-xs text-p2p-muted border border-p2p-border/50">
                        <AlertCircle className="w-3 h-3" /> Emergency
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label htmlFor="startDate" className="text-p2p-muted">
                  {isRecurring 
                    ? (type === 'income' ? 'Next pay date' : 'First payment date')
                    : 'Date'
                  }
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="h-12 rounded-xl border-p2p-border focus:border-p2p-teal"
                  required
                  data-testid="input-date"
                />
              </div>

              {/* Category (for bills only) */}
              {type === 'bill' && (
                <div className="space-y-2">
                  <Label className="text-p2p-muted">Category</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(val) => setFormData({ ...formData, category: val })}
                  >
                    <SelectTrigger className="h-12 rounded-xl border-p2p-border" data-testid="select-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Apply to Debt Toggle - inside Bill form */}
              {type === 'bill' && (
                <div className={`p-4 rounded-2xl border-2 transition-all ${
                  formData.isDebtPayment 
                    ? 'bg-purple-50 border-purple-200' 
                    : 'bg-white border-p2p-border'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        formData.isDebtPayment ? 'bg-purple-100' : 'bg-gray-100'
                      }`}>
                        <CreditCard className={`w-5 h-5 ${
                          formData.isDebtPayment ? 'text-purple-600' : 'text-gray-400'
                        }`} />
                      </div>
                      <div>
                        <p className={`font-medium ${
                          formData.isDebtPayment ? 'text-purple-700' : 'text-p2p-muted'
                        }`}>
                          Apply to a debt
                        </p>
                        <p className="text-xs text-p2p-muted">
                          Track this payment against a debt balance
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.isDebtPayment || false}
                      onCheckedChange={(checked) => setFormData({ ...formData, isDebtPayment: checked })}
                      data-testid="toggle-debt-payment"
                    />
                  </div>
                </div>
              )}

              {/* Debt-specific fields - Show when toggle is ON */}
              {type === 'bill' && formData.isDebtPayment && (
                <div className="space-y-4 p-4 rounded-xl bg-purple-50 border border-purple-200 animate-fade-in">
                  <p className="text-sm font-medium text-purple-700 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Debt details
                  </p>
                  
                  <div className="space-y-2">
                    <Label htmlFor="totalDebt" className="text-p2p-muted text-sm">Total amount owed</Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-p2p-muted">
                        {user?.currency === 'USD' ? '$' : user?.currency === 'EUR' ? '€' : '£'}
                      </span>
                      <Input
                        id="totalDebt"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.totalDebt}
                        onChange={(e) => setFormData({ ...formData, totalDebt: e.target.value })}
                        placeholder="0.00"
                        className="h-11 pl-8 rounded-xl border-purple-200 bg-white"
                        data-testid="input-total-debt"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="interestRate" className="text-p2p-muted text-sm">Interest rate (APR)</Label>
                    <div className="relative">
                      <Input
                        id="interestRate"
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={formData.interestRate}
                        onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
                        placeholder="0.0"
                        className="h-11 pr-8 rounded-xl border-purple-200 bg-white"
                        data-testid="input-interest-rate"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-p2p-muted">%</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className={`w-full h-14 rounded-2xl text-base font-semibold shadow-lg transition-all active:scale-[0.98] ${
                  type === 'income' 
                    ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20' 
                    : formData.isDebtPayment
                      ? 'bg-purple-600 hover:bg-purple-700 shadow-purple-600/20'
                      : 'bg-red-500 hover:bg-red-600 shadow-red-500/20'
                }`}
                data-testid="submit-btn"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : (
                  `Add ${type === 'income' ? 'Income' : formData.isDebtPayment ? 'Debt Payment' : 'Bill'}`
                )}
              </Button>
            </form>
          </div>
        )}
      </div>
    </Sidebar>
  );
}
